import { useEffect, useRef, useState } from 'react'
import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import {
  STANDARD_SOURCE_LANGUAGE,
  isSupportedLanguage,
  type SupportedLanguage,
} from '@/shared/config/languages'
import { useListAssociatedPartiesForProduct } from '@/shared/hooks/api/associatedParties'
import {
  useGetRiskAssessmentDocument,
  useUpdateRiskAssessmentDocument,
  useCreateRiskAssessmentDocumentVersion,
  useStartRiskAssessmentGeneration,
  useExportRiskAssessmentPdf,
  useGetMachineryDirectiveCompliance,
  type RiskAssessmentSectionRecord,
  type RiskAssessmentStreamEvent,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useGetProduct } from '@/shared/hooks/api/product'
import { buildRiskAssessmentPdfFilename } from '@/shared/utils/pdf-export-filenames'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { ComplianceDocumentGenerateMenu } from '../../components/ComplianceDocumentGenerateMenu'
import { SectionRegenerateConfirmDialog } from '../../components/SectionRegenerateConfirmDialog'
import { RewriteInsufficientSourcesDialog } from '../../components/RewriteInsufficientSourcesDialog'
import { RewriteInstructionRefusedDialog } from '../../components/RewriteInstructionRefusedDialog'
import { useComplianceDocumentRewrite } from '../../hooks/useComplianceDocumentRewrite'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { ComplianceDocumentManifestProvider } from '../../context/ComplianceDocumentManifestContext'
import type { ComplianceDocumentViewHandle } from '../../types/complianceDocumentViewHandle'
import {
  buildCompliancePdfCoverMarkdown,
  formatCompliancePdfDate,
} from '../../utils/buildCompliancePdfCoverMarkdown'
import { parseComplianceDocumentSavedAt } from '../../utils/formatComplianceDocumentLastSavedAt'
import { resolveComplianceDocumentSectionsToGenerate } from '../../utils/resolveComplianceDocumentSectionsToGenerate'
import { Button } from '@/components/ui/button'
import { RewriteDrawer } from './RewriteDrawer'
import { RiskAssessmentDocumentView } from './RiskAssessmentDocumentView'
import {
  RISK_ASSESSMENT_SECTION_8_ID,
  shouldShowRiskAssessmentSection8StaleCallout,
} from './riskAssessmentSection8StaleCallout'
import {
  ComplianceDocumentStreamingPanel,
  COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS,
} from '../../../shared/compliance-document/ComplianceDocumentStreamingPanel'

export function RiskAssessmentPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const complianceId = params.machineryDirectiveComplianceId!
  const productId = params.productId!
  const { t, i18n } = useTranslation([
    'machineryDirectiveCompliance',
    'associatedParties',
    'countries',
  ])

  const { data: document, isLoading } =
    useGetRiskAssessmentDocument(complianceId)
  const { data: compliance } = useGetMachineryDirectiveCompliance(complianceId)
  const { data: product } = useGetProduct(productId)
  const { data: associatedPartyAssignments = [] } =
    useListAssociatedPartiesForProduct(productId)
  const updateDocument = useUpdateRiskAssessmentDocument(complianceId)
  const createDocumentVersion =
    useCreateRiskAssessmentDocumentVersion(complianceId)
  const startGeneration = useStartRiskAssessmentGeneration(complianceId)
  const exportPdf = useExportRiskAssessmentPdf(complianceId)

  const [isGenerating, setIsGenerating] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [streamingSectionId, setStreamingSectionId] = useState<string | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const [sections, setSections] = useState<RiskAssessmentSectionRecord[]>([])
  const [contentSyncKey, setContentSyncKey] = useState(0)

  const [pendingFullRegenerate, setPendingFullRegenerate] = useState<{
    language: SupportedLanguage
  } | null>(null)
  const [pendingRegenerateSectionId, setPendingRegenerateSectionId] = useState<
    string | null
  >(null)
  const [generationLanguage, setGenerationLanguage] =
    useState<SupportedLanguage>(STANDARD_SOURCE_LANGUAGE)
  const documentViewRef = useRef<ComplianceDocumentViewHandle>(null)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)

  const displaySectionsForRewrite =
    sections.length > 0 ? sections : (document?.sections ?? [])

  const rewrite = useComplianceDocumentRewrite({
    complianceId,
    documentType: 'RISK_ASSESSMENT',
    sections: displaySectionsForRewrite,
    translate: (key) => t(`riskAssessmentPage.${key}`),
    getFlushedSectionMarkdown: (sectionId) => {
      const flushedSections =
        documentViewRef.current?.flushSectionsToState() ??
        displaySectionsForRewrite
      return (
        flushedSections.find((entry) => entry.sectionId === sectionId)
          ?.markdown ?? null
      )
    },
    onOpenRewritePanel: () => {
      documentViewRef.current?.cancelAutosave()
    },
  })

  useEffect(() => {
    if (document == null) {
      if (
        compliance?.contentLanguage != null &&
        isSupportedLanguage(compliance.contentLanguage)
      ) {
        setGenerationLanguage(compliance.contentLanguage)
      }
      return
    }
    setSections(document.sections ?? [])
    setContentSyncKey((previous) => previous + 1)
    setLastSavedAt(
      parseComplianceDocumentSavedAt(document.generatedAt, document.createdAt)
    )
    if (
      document.language != null &&
      isSupportedLanguage(document.language)
    ) {
      setGenerationLanguage(document.language)
    }
  }, [
    document?.id,
    document?.versionNumber,
    document?.language,
    compliance?.contentLanguage,
  ])

  const emptyStateStreamingStatusLabel =
    streamingSectionId != null
      ? t('riskAssessmentPage.generatingSection', {
          section: streamingSectionId,
        })
      : t('riskAssessmentPage.generating')

  const runGeneration = (payload: {
    language?: SupportedLanguage
    fullDocument?: boolean
    sectionIds?: string[]
  }) => {
    setError(null)
    setStreamingContent('')
    setStreamingSectionId(null)
    setIsGenerating(true)

    const sectionsToGenerate = resolveComplianceDocumentSectionsToGenerate({
      fullDocument: payload.fullDocument,
      sectionIds: payload.sectionIds,
    })

    startGeneration.mutate(
      {
        sectionsToGenerate,
        language:
          payload.fullDocument === true
            ? (payload.language ?? generationLanguage)
            : undefined,
        onEvent: (event: RiskAssessmentStreamEvent) => {
          switch (event.type) {
            case 'document_section.started':
              setStreamingSectionId(event.section)
              setStreamingContent('')
              break
            case 'document_section.progress':
              if (event.text) {
                setStreamingContent((prev) => prev + event.text)
              }
              break
            case 'document_section.completed':
              break
            case 'document.completed':
              break
            case 'error':
              setError(event.error)
              break
            default:
              break
          }
        },
        onComplete: () => {
          setIsGenerating(false)
          toast.success(t('riskAssessmentPage.successToast'))
          window.setTimeout(() => {
            setStreamingContent('')
            setStreamingSectionId(null)
          }, COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS)
        },
      },
      {
        onError: (err) => {
          setIsGenerating(false)
          setError(err.message)
          toast.error(t('riskAssessmentPage.generateFailed'))
          window.setTimeout(() => {
            setStreamingContent('')
            setStreamingSectionId(null)
          }, COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS)
        },
      }
    )
  }

  const startGenerationWithSave = async (payload: {
    language?: SupportedLanguage
    fullDocument?: boolean
    sectionIds?: string[]
  }) => {
    try {
      await documentViewRef.current?.flushAndAutosaveSilent()
    } catch {
      return
    }
    runGeneration(payload)
  }

  const handleGenerate = (payload: {
    language?: SupportedLanguage
    fullDocument?: boolean
  }) => {
    const isFullDocumentRegenerate = payload.fullDocument === true
    const hasExistingDocument =
      sections.length > 0 || (document?.sections?.length ?? 0) > 0

    if (isFullDocumentRegenerate && hasExistingDocument) {
      setPendingFullRegenerate({
        language: payload.language ?? generationLanguage,
      })
      return
    }

    void startGenerationWithSave(payload)
  }

  const handleRegenerateSection = (sectionId: string) => {
    setPendingRegenerateSectionId(sectionId)
  }

  const handleConfirmSectionRegenerate = (sectionId: string) => {
    setPendingRegenerateSectionId(null)
    void startGenerationWithSave({
      fullDocument: false,
      sectionIds: [sectionId],
    })
  }

  const handleConfirmFullRegenerate = () => {
    if (pendingFullRegenerate == null) {
      return
    }
    const language = pendingFullRegenerate.language
    setPendingFullRegenerate(null)
    void startGenerationWithSave({ fullDocument: true, language })
  }

  const handleExportPdf = async () => {
    if (!document) {
      toast.error(t('riskAssessmentPage.exportFailed'))
      throw new Error('Risk assessment document not found')
    }
    const manufacturerAssignment = associatedPartyAssignments.find(
      (assignment) => assignment.role === 'MANUFACTURER'
    )
    const manufacturerParty = manufacturerAssignment?.associatedParty ?? null
    const productName = product?.name ?? ''
    const model = product?.productModel ?? ''
    const yearRaw = product?.yearOfManufacture
    const year = yearRaw == null ? '' : String(yearRaw)
    const timestampIso =
      document.generatedAt ?? document.createdAt ?? new Date().toISOString()

    const coverMarkdown = buildCompliancePdfCoverMarkdown({
      documentTitle: t('riskAssessmentPage.title'),
      labels: {
        manufacturer: t('pdfExportCover.manufacturer'),
        productName: t('pdfExportCover.productName'),
        model: t('pdfExportCover.model'),
        year: t('pdfExportCover.year'),
        documentCreated: t('pdfExportCover.documentCreated'),
        notAvailable: t('pdfExportCover.notAvailable'),
      },
      manufacturerParty,
      getCountryName: (code) => t(code, { ns: 'countries' }) || code,
      translateAssociatedParty: (key, options) =>
        t(key, { ns: 'associatedParties', ...options }),
      productName,
      model,
      year,
      documentTimestampIso: timestampIso,
      formatDate: (iso) => formatCompliancePdfDate(iso, i18n.language),
    })

    const exportFilename = buildRiskAssessmentPdfFilename(new Date())

    try {
      await exportPdf.mutateAsync({
        coverMarkdown,
        documentName: exportFilename,
      })
      toast.success(t('riskAssessmentPage.pdfExportedSuccess'))
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('riskAssessmentPage.exportFailed')
      )
      throw err
    }
  }

  const handleAutosave = async (sections: RiskAssessmentSectionRecord[]) => {
    try {
      await updateDocument.mutateAsync({
        sections,
        skipCacheInvalidation: true,
      })
      setLastSavedAt(new Date())
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('riskAssessmentPage.saveFailed')
      )
      throw err
    }
  }

  const handleCreateVersion = async (
    sections: RiskAssessmentSectionRecord[]
  ) => {
    try {
      const created = await createDocumentVersion.mutateAsync({ sections })
      setSections(created.sections ?? [])
      setContentSyncKey((previous) => previous + 1)
      setLastSavedAt(
        parseComplianceDocumentSavedAt(
          created.generatedAt,
          created.createdAt
        ) ?? new Date()
      )
      toast.success(
        t('riskAssessmentPage.saveVersionSuccess', {
          versionNumber: created.versionNumber,
        })
      )
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('riskAssessmentPage.saveFailed')
      )
      throw err
    }
  }

  const openRewritePanel = rewrite.openRewritePanel

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  const hasError = error != null
  const hasDocument =
    sections.length > 0 || (document?.sections?.length ?? 0) > 0
  const displaySections =
    sections.length > 0 ? sections : (document?.sections ?? [])
  const showSection8StaleCallout = shouldShowRiskAssessmentSection8StaleCallout(
    document?.isSection8Expired
  )

  const pendingRegenerateSection = pendingRegenerateSectionId
    ? displaySections.find(
        (section) => section.sectionId === pendingRegenerateSectionId
      )
    : undefined

  const fullRegenerateConfirmDialog = (
    <ConfirmDialog
      open={pendingFullRegenerate != null}
      onOpenChange={(open) => {
        if (!open) {
          setPendingFullRegenerate(null)
        }
      }}
      handleConfirm={handleConfirmFullRegenerate}
      title={t('riskAssessmentPage.fullRegenerateCustomSectionsDialog.title')}
      desc={t(
        'riskAssessmentPage.fullRegenerateCustomSectionsDialog.description'
      )}
      cancelBtnText={t('riskAssessmentPage.cancel')}
      confirmText={t(
        'riskAssessmentPage.fullRegenerateCustomSectionsDialog.confirm'
      )}
      destructive
      isLoading={isGenerating || startGeneration.isPending}
    />
  )

  const sectionRegenerateConfirmDialog = (
    <SectionRegenerateConfirmDialog
      open={pendingRegenerateSectionId != null}
      onOpenChange={(open) => {
        if (!open) {
          setPendingRegenerateSectionId(null)
        }
      }}
      sectionId={pendingRegenerateSectionId}
      sectionTitle={pendingRegenerateSection?.title ?? null}
      sectionKind={pendingRegenerateSection?.kind}
      documentType='risk_assessment'
      translationKeyPrefix='riskAssessmentPage'
      onConfirm={handleConfirmSectionRegenerate}
      isLoading={isGenerating || startGeneration.isPending}
    />
  )

  const renderGenerateMenu = (options: {
    isRegenerate: boolean
    fullDocumentOnly?: boolean
    size?: 'default' | 'sm'
    className?: string
    withFileIcon?: boolean
  }) => (
    <ComplianceDocumentGenerateMenu
      {...options}
      translationKeyPrefix='riskAssessmentPage'
      generationLanguage={generationLanguage}
      onGenerationLanguageChange={setGenerationLanguage}
      onGenerate={handleGenerate}
      isGeneratePending={
        startGeneration.isPending ||
        updateDocument.isPending ||
        createDocumentVersion.isPending
      }
      isExportPending={exportPdf.isPending}
    />
  )

  const section8StaleCallout = showSection8StaleCallout ? (
    <Alert variant='warning' className='mb-4 text-left'>
      <AlertTitle>
        {t('riskAssessmentPage.section8StaleCallout.title')}
      </AlertTitle>
      <div className='col-span-2 w-full justify-between xl:flex'>
        <AlertDescription>
          {t('riskAssessmentPage.section8StaleCallout.body')}
        </AlertDescription>
        <AlertAction>
          <Button
            type='button'
            size='xs'
            variant='warning'
            disabled={
              isGenerating ||
              startGeneration.isPending ||
              exportPdf.isPending
            }
            onClick={() => {
              handleRegenerateSection(RISK_ASSESSMENT_SECTION_8_ID)
            }}
          >
            {t('riskAssessmentPage.section8StaleCallout.regenerateButton')}
          </Button>
        </AlertAction>
      </div>
    </Alert>
  ) : null

  const pageHeader = (
    <ComplianceSubPageHeader>
      <ComplianceSubPageTitle>
        {t('riskAssessmentPage.title')}
      </ComplianceSubPageTitle>
      {hasError && (
        <Alert variant='destructive'>
          <AlertTitle>{error}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </ComplianceSubPageHeader>
  )

  const rewriteBubbleMenu = (
    <Button
      type='button'
      variant='ghost'
      size='sm'
      onClick={openRewritePanel}
    >
      {t('riskAssessmentPage.rewrite')}
    </Button>
  )

  const pageContent = hasDocument ? (
    <>
      <div className='flex h-[calc(100dvh-11rem)] min-h-80 flex-col overflow-hidden'>
        {pageHeader}
        {section8StaleCallout}
        <RiskAssessmentDocumentView
          ref={documentViewRef}
          complianceId={complianceId}
          sections={displaySections}
          onSectionsChange={setSections}
          onAutosave={handleAutosave}
          onCreateVersion={handleCreateVersion}
          isAutosaving={updateDocument.isPending}
          isCreatingVersion={createDocumentVersion.isPending}
          lastSavedAt={lastSavedAt}
          isGenerating={isGenerating}
          onExportPdf={handleExportPdf}
          isExportPending={exportPdf.isPending}
          generateMenu={renderGenerateMenu({
            isRegenerate: true,
            size: 'sm',
          })}
          onRegenerateSection={handleRegenerateSection}
          contentSyncKey={contentSyncKey}
          streamingSectionId={streamingSectionId}
          streamingContent={streamingContent}
          onFocusedEditorChange={rewrite.handleFocusedEditorChange}
          bubbleMenuContent={rewriteBubbleMenu}
          bubbleMenuHidden={rewrite.isRewritePanelOpen}
          rewritePanelOpen={rewrite.isRewritePanelOpen}
        />
      </div>
      <RewriteDrawer
        isOpen={rewrite.isRewritePanelOpen}
        onClose={rewrite.closeRewritePanel}
        selectedText={rewrite.selectedTextInPanel}
        instruction={rewrite.rewriteInstructionValue}
        onInstructionChange={rewrite.setRewriteInstructionValue}
        onRewrite={rewrite.handleRewrite}
        isRewritePending={rewrite.isRewritePending}
        result={rewrite.rewriteResult}
        hasResult={(rewrite.rewriteResult?.length ?? 0) > 0}
        onAccept={rewrite.handleAcceptRewrite}
        onReject={rewrite.handleRejectRewrite}
        blockedMessage={rewrite.rewriteBlockedMessage}
      />
      <RewriteInsufficientSourcesDialog
        open={rewrite.isInsufficientSourcesDialogOpen}
        onOpenChange={rewrite.setIsInsufficientSourcesDialogOpen}
        translationKeyPrefix='riskAssessmentPage'
        blockReason={rewrite.insufficientSourcesBlockReason}
        availableSourceNames={rewrite.insufficientSourcesAvailableNames}
      />
      <RewriteInstructionRefusedDialog
        open={rewrite.isInstructionRefusedDialogOpen}
        onOpenChange={rewrite.setIsInstructionRefusedDialogOpen}
        translationKeyPrefix='riskAssessmentPage'
      />
      {fullRegenerateConfirmDialog}
      {sectionRegenerateConfirmDialog}
    </>
  ) : (
    <>
      <div className='flex h-full flex-col space-y-4'>
        {pageHeader}

        <ComplianceDocumentStreamingPanel
          isOpen={isGenerating}
          statusLabel={emptyStateStreamingStatusLabel}
          streamingContent={streamingContent}
        />

        {!isGenerating && !error && (
          <Empty className='min-h-[320px] rounded-lg border border-dashed'>
            <EmptyHeader>
              <EmptyMedia variant='icon'>
                <FileText className='size-10' />
              </EmptyMedia>
              <EmptyTitle>{t('riskAssessmentPage.title')}</EmptyTitle>
              <EmptyDescription>
                {t('riskAssessmentPage.emptyStateMessage')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              {renderGenerateMenu({
                isRegenerate: false,
                fullDocumentOnly: true,
                size: 'sm',
                className: 'gap-2',
                withFileIcon: true,
              })}
            </EmptyContent>
          </Empty>
        )}
      </div>
      {fullRegenerateConfirmDialog}
      {sectionRegenerateConfirmDialog}
    </>
  )

  const manifestLocale =
    document?.language != null && isSupportedLanguage(document.language)
      ? document.language
      : generationLanguage

  return (
    <ComplianceDocumentManifestProvider
      documentKind='risk_assessment'
      locale={manifestLocale}
    >
      {pageContent}
    </ComplianceDocumentManifestProvider>
  )
}
